import { makeAutoObservable, runInAction } from 'mobx';
import { uniqBy } from 'lodash-es';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ReportVersionDto } from '@src/api';

export type ReportVersionDtoDefId = Omit<ReportVersionDto, 'id' | 'code'> & { id: string; code: string };

export type Filters = {
  reportGroup?: string;
  reportType?: string;
};

class ReportVersionsStore {
  reportVersions: ReportVersionDtoDefId[] = [];

  reportVersionsWithStructure: ReportVersionDtoDefId[] = [];

  reportVersionsWithStructureByReportGroup: ReportVersionDtoDefId[] = [];

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: ReportVersionDtoDefId | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  api: CmApi;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: ReportVersionDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportVersions.getReportVersions();

    runInAction(() => {
      this.reportVersions = uniqBy(
        data?.map((item) => ({ ...item, id: item.code ?? '', code: item.code ?? '' })) ?? [],
        'code',
      );
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  getReportVersionsWithStructure = async (organizationCode?: string): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportVersions.getReportVersions({ withStructure: true, organizationCode });

    runInAction(() => {
      this.reportVersionsWithStructure = uniqBy(
        data?.map((item) => ({ ...item, id: item.code ?? '', code: item.code ?? '' })) ?? [],
        'code',
      );
    });
    preloaderStore.preloader(false);
  };

  getReportVersionsWithStructureByReportGroup = async (reportGroupCode: string, organizationCode: string) => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportVersions.getReportVersions({
      withStructure: true,
      organizationCode,
      reportGroupCode,
    });

    runInAction(() => {
      this.reportVersionsWithStructureByReportGroup =
        data?.map((item) => ({ ...item, id: item.code ?? '', code: item.code ?? '' })) ?? [];
    });
    preloaderStore.preloader(false);
  };

  groupDelete = async (items: ReportVersionDtoDefId[]) => {
    preloaderStore.preloader(true, 'удаление...');
    const deleteCodes = items.map((version) => version.code);
    await this.api.reportVersions.deleteReportVersion(deleteCodes);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
  };
}

export const reportVersionsStore = new ReportVersionsStore({ api: cmApi });
